import { getApolloClient } from '../lib/getApolloClient';
import {
  GET_CLIENT,
  GET_ALL_CLIENTS_FOR_TENANT,
  CREATE_CLIENT,
  UPDATE_CLIENT,
  SET_CLIENT_STATUS,
  SET_CLIENT_RATE_CARD,
} from './client.gql';
import { GET_USERS_BY_CLIENT } from '../user/user.gql';
import {
  GET_OUTSTANDING_INVITES_FOR_CLIENT,
  REVOKE_INVITE,
} from '../invite/invite.gql';
import {
  Client,
  ClientInput,
  ClientRateCardInput,
  Invite,
  User,
  ClientStatus,
} from '../generated/gql-types';

const getClientById = async (id: string): Promise<Client | null> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_CLIENT,
      variables: { id },
    });
    return data?.getClient;
  } catch (error) {
    console.error('Error fetching client by id:', error);
    return null;
  }
};

const getAllClientsForTenant = async (
  tenantId?: string,
  limit: number = 20,
  lastEvaluatedKey?: string,
  forceFetchAll?: boolean,
): Promise<{
  items: Client[];
  hasMore: boolean;
  lastEvaluatedKey: string | null;
}> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_ALL_CLIENTS_FOR_TENANT,
      variables: forceFetchAll
        ? { tenantId, forceFetchAll: true }
        : {
            tenantId,
            limit,
            lastEvaluatedKey,
          },
    });
    return data.getAllClientsForTenant;
  } catch (error) {
    console.error('Error fetching clients for tenant:', error);
    return { items: [], hasMore: false, lastEvaluatedKey: null };
  }
};

const createClient = async (
  details: ClientInput,
): Promise<{ success: boolean; client?: Client; error?: string }> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: CREATE_CLIENT,
      variables: {
        details,
      },
    });

    if (!data?.createClient) {
      return { success: false, error: 'Failed to create client' };
    }

    return { success: true, client: data.createClient };
  } catch (error) {
    console.error('Error creating client:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
};

const getUsersByClient = async (clientId: string): Promise<User[]> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_USERS_BY_CLIENT,
      variables: {
        clientId,
      },
    });
    return data?.getUsersByClientId || [];
  } catch (error) {
    console.error('Error fetching users by client:', error);
    return [];
  }
};

const getOutstandingInvitesForClient = async (
  clientId: string,
): Promise<Invite[]> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_OUTSTANDING_INVITES_FOR_CLIENT,
      variables: {
        clientId,
      },
    });
    return data?.getOutstandingInvitesForClient || [];
  } catch (error) {
    console.error('Error fetching invites for client:', error);
    return [];
  }
};

const updateClient = async (
  id: string,
  updatedClient: Partial<Client>,
): Promise<{ success: boolean; client?: Client; error?: string }> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: UPDATE_CLIENT,
      variables: { id, details: updatedClient },
    });

    if (!data?.updateClient) {
      return { success: false, error: 'Failed to update client' };
    }

    return { success: true, client: data.updateClient };
  } catch (error) {
    console.error('Error updating client:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
};

const setClientStatus = async (
  id: string,
  status: ClientStatus,
): Promise<{ success: boolean; client?: Client; error?: string }> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: SET_CLIENT_STATUS,
      variables: { id, status },
    });

    if (!data?.setClientStatus) {
      return { success: false, error: 'Failed to set client status' };
    }

    return { success: true, client: data.setClientStatus };
  } catch (error) {
    console.error('Error setting client status:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
};

const setClientRateCard = async (
  clientId: string,
  rateCard: ClientRateCardInput,
): Promise<{ success: boolean; client?: Client; error?: string }> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: SET_CLIENT_RATE_CARD,
      variables: { clientId, rateCard },
    });

    if (!data?.setClientRateCard) {
      return { success: false, error: 'Failed to set client rate card' };
    }

    return { success: true, client: data.setClientRateCard };
  } catch (error) {
    console.error('Error setting client rate card:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
};

const revokeInvite = async (
  id: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data } = await getApolloClient().mutate({
      mutation: REVOKE_INVITE,
      variables: { id },
    });

    if (!data?.revokeInvite) {
      return { success: false, error: 'Failed to revoke invite' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error revoking invite:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
};

export const clientActions = {
  getClientById,
  getAllClientsForTenant,
  createClient,
  getUsersByClient,
  getOutstandingInvitesForClient,
  updateClient,
  setClientStatus,
  setClientRateCard,
  revokeInvite,
};
